/**
 * @Description 拓店共通版-定制组件特殊处理
 */

import { ControlType, parseValueCatch, readInDataWhenTargetHide, setComValue } from './config';
import { standardSurroundSearch } from '@/common/api/surround';
import {
  chancepointPlanCluster,
  chancepointModelCluster,
  chancePointEstimatedSales,
  chancepointFlowScore,
  chancePointEvaluation
} from '@/common/api/expandStore/chancepoint';
import { areaList } from '@/common/api/common';
import { findReplaceDistrcitName, getLngLatAddress } from '@/common/utils/map';
import { isArray, isDef, isNotEmpty, isNotEmptyAny } from '@lhb/func';
import moment from 'moment';
import { getCityForAMapData, removePcdFromAddress } from '@/common/utils/ways';
import { getAMapCityName } from '../../Map/V2AMap/utils/amap';

/** 机会点名称字段的唯一标识符 */
export const chanceNameIdentification = 'basicChancePointName';
/** 省市区组件的唯一标识符 */
export const pcdIdentification = 'area';
/** 地址组件的唯一标识符 */
export const addressIdentification = 'basicAddress';
/** 网规组件的唯一标识符 */
export const networkPlanningIdentification = 'networkPlanning';
/** 销售额预测的唯一标识符 */
export const estimatedDailyIdentification = 'estimatedDailyRevenue';
/** 客群质量评分的唯一标识符 */
export const customerQualityScoreIdentification = 'customerQualityScore';
/** 商圈匹配信息的唯一标识符 */
export const matchBusinessAreaIdentification = 'matchBusinessArea';
/** 门店类型专用识别符 */
export const identificationStoreType = 'storeTypeStandardEdition';
/** 益禾堂美团报告专用识别符 */
export const meituanReportYHTIdentification = 'meituanReportYHT';
/** 益禾堂预估日商专用识别符 */
export const revenueYHTIdentification = 'revenueYHT';
/** 益禾堂外卖系数的专用识别符 */
export const takeawayfactorIdentification = 'takeawayfactor';
/** 益禾堂外卖系数说明的专用识别符 */
export const explanationYHTIdentification = 'explanationYHT';
/** 森马定制客流计数-日均客流专用标识符 */
export const passengerFlowAverageIdentification = 'dailyPassengerFlowSemir';

/** 匹配商圈组件内的加载中临时子字段专用识别符 */
export const matchBusinessAreaLoadingIdentification = 'matchBusinessAreaLoading';

// 无论任何情况下，机会点表单必须检查必填的特殊字段
export const AlwaysRequiredChanceIndentifications = [
  chanceNameIdentification,
  pcdIdentification,
  addressIdentification
];

/**
 * @description 详细地址变更时周边查询组件更新
 * @param addressInfo 地址对象{adcode,lat,lng,cityId,cityName,address}
 * @return
 */
const updateAddressSurround = async (tiledFormDataRef: any,
  form, addressInfo: any) => {
  const targetCom = tiledFormDataRef.current.find((item: any) => item.controlType === ControlType.SURROUND_SEARCH.value);
  if (!targetCom || !targetCom.restriction) return;

  const restriction = JSON.parse(targetCom.restriction);
  const radius = restriction.radius || 0;
  const tplId = restriction.tplId || 0;
  const surroundData = await standardSurroundSearch({ radius, tplId, lat: addressInfo.lat, lng: addressInfo.lng });
  if (!surroundData) return;

  const surround = Array.isArray(surroundData) ? surroundData.map(itm => ({
    categoryName: itm.categoryName,
    poiName: Array.isArray(itm.poiName) ? itm.poiName : [],
    text: Array.isArray(itm.poiName) ? itm.poiName.join('、') : ''
  })) : [];

  const surroundValue = {
    ...addressInfo,
    surround
  };
  setComValue(targetCom, surroundValue); // 存储周边查询数据
  // setFieldValue 不会触发onValuesChange
  form.setFieldValue(targetCom.identification, surroundValue.address); // 组件显示值
};

/**
 * @description 详细地址变更时商圈规划组件更新
 * @param addressInfo 地址对象{adcode,lat,lng,cityId,cityName,address}
 * @return
 */
const updateAddressBusiPlan = async (
  tiledFormDataRef: any,
  form,
  addressInfo: any,
  callback: Function
) => {
  const targetCom = tiledFormDataRef.current.find((item: any) => item.identification === networkPlanningIdentification && item.controlType === ControlType.BUSINESS_PLANNING.value);
  if (!targetCom) return;

  const { lat, lng } = addressInfo;
  const data = await chancepointPlanCluster({ lat, lng });

  // 获取之前设置的集客点id，存到组件新数据中
  const oldVal = parseValueCatch(targetCom);
  const oldPlanSpotId = oldVal?.planSpotId;
  const targetSpot = oldPlanSpotId && isArray(data?.planSpots) ? data.planSpots.find((itm: any) => itm.id === oldPlanSpotId) : null;
  // 之前选择的集客点id能匹配到当前商圈信息接口返回的数据，则继续保持，否则当作没有选中项
  const planSpotId = targetSpot ? targetSpot.id : null;
  const planSpotName = targetSpot ? targetSpot.name : null;

  const compValue = {
    ...data,
    lat,
    lng,
    planSpotId,
    planSpotName
  };
  setComValue(targetCom, compValue); // 存储数据

  // 将商圈规划数据传入组件
  callback(compValue);
};

/**
 * @description 详细地址变更时商圈信息组件更新
 * @param addressInfo 地址对象{adcode,lat,lng,cityId,cityName,address}
 * @return
 */
const updateAddressBusiInfo = async (
  tiledFormDataRef: any,
  form,
  addressInfo: any,
  callback: Function,
  isInit = false,
) => {
  const targetCom = tiledFormDataRef.current.find((item: any) => item.controlType === ControlType.MATCH_BUSINESS_CIRCLE.value);
  if (!targetCom) return;

  const { restriction, textValue } = targetCom;
  const modelId = restriction ? JSON.parse(restriction)?.modelId : null;

  const { lat, lng } = addressInfo;
  const _textValue = JSON.parse(textValue);
  const data = await chancepointModelCluster({ lat, lng, modelConfigId: modelId });
  // let compValue = { ...data, lat, lng };
  let compValue = {
    ..._textValue,
    ...(isNotEmptyAny(data) ? data : { isInCluster: false }), // 处在区域外获取不到信息，手动设置isInCluster
    lat,
    lng,
    modelConfigId: modelId,
  };
  // 判断下选项中是否有选中的集客点id
  // if (isNotEmpty(data?.savePlanSpot) && data?.savePlanSpot?.modelClusterId === data.clusterId) {
  //   compValue = { ...data, lat, lng };
  // }

  if (!compValue.isInCluster) {
    // 不在开店区域内
    const spotData = await chancePointEvaluation({
      type: 1, // 点位测评类型 默认1 街边店
      lng,
      lat,
      modelConfigId: modelId,
      radius: 500, // 默认500m
    });
    compValue = {
      isInCluster: false,
      lat,
      lng,
      modelConfigId: modelId,
      clusterScore: spotData?.marketScore || '', // 特殊赋值，使用点位测评字段值
      areaName: spotData?.areaName || '',
      cityId: spotData?.cityId || '',
      cityName: spotData?.cityName || '',
    };
  }
  setComValue(targetCom, compValue); // 存储数据
  // 将商圈规划数据传入组件
  callback({
    ...compValue,
    isInit
  });
};

/**
* @description 销售额、销售额上限值、外卖预估预测
* @param addressInfo 地址信息
* @param businessInfo 商圈匹配信息，有匹配组件时，数据分为在商圈内/在商圈外；无匹配组件时，参数值为null;
* @return
*/
const updateEstimatedSales = async (
  tiledFormDataRef: any,
  form,
  addressInfo: any,
  businessInfo?: any,
) => {
  // 这里的用法就不对，应该用ControlType.SALE_AMOUNT这种控件类型，而不是使用定制字段，estimatedDailyIdentification还参与了计算公式的定制逻辑，这里就先不改了
  const dailyRevenueCom = tiledFormDataRef.current.find((item: any) => item.identification === estimatedDailyIdentification && item.controlType === ControlType.INPUT_NUMBER.value);

  const saleMaxCom = tiledFormDataRef.current.find((item: any) => item.controlType === ControlType.ESTIMATE_SALES_TWO_YHT.value);

  const deliveryCom = tiledFormDataRef.current.find((item: any) => item.controlType === ControlType.ESTIMATE_SALES_DELIVERY_YHT.value);

  // 无任何一个相关组件，没必要请求接口结束处理
  if (!dailyRevenueCom && !saleMaxCom && !deliveryCom) return;

  // 详细地址信息
  const { cityId, lat, lng } = addressInfo;
  const { clusterId: modelClusterId, modelConfigId } = businessInfo || {};
  // 不属于中国城市的时候，设置组件值为null，避免接口请求报错
  if (!isNotEmpty(cityId) || !isNotEmpty(lat) || !isNotEmpty(lng)) {
    fillInEstimateComp(form, dailyRevenueCom, null);
    fillInEstimateComp(form, saleMaxCom, null);
    fillInEstimateComp(form, deliveryCom, null);
    return;
  }

  // 实时查询当地预估销售额
  const data = await chancePointEstimatedSales({ modelClusterId, modelConfigId, lat, lng }, { needCancel: false });
  let saleAmountVal: any = null;
  let maxSaleAmountVal: any = null;
  let takeawaySalesVal: any = null;
  if (isNotEmptyAny(data)) {
    const { saleAmount, maxSaleAmount, takeawaySales } = data; // process.env.NODE_ENV === 'development' ? 8888 : null
    saleAmountVal = isDef(saleAmount) ? parseInt(saleAmount) : null;
    maxSaleAmountVal = isDef(maxSaleAmount) ? parseInt(maxSaleAmount) : null;
    takeawaySalesVal = isDef(takeawaySales) ? parseInt(takeawaySales) : null;
  }
  fillInEstimateComp(form, dailyRevenueCom, saleAmountVal);
  fillInEstimateComp(form, saleMaxCom, maxSaleAmountVal);
  fillInEstimateComp(form, deliveryCom, takeawaySalesVal);

  if (dailyRevenueCom && isNotEmpty(saleAmountVal)) {
    readInDataWhenTargetHide(tiledFormDataRef.current, {
      label: `${saleAmountVal}元（${dailyRevenueCom?.anotherName || dailyRevenueCom?.name || '营业额预估值'}）`,
      value: dailyRevenueCom.identification,
      detailShowVal: `${saleAmountVal}元`,
      countValue: saleAmountVal, // 服务端用
    });
  }
  if (saleMaxCom && isNotEmpty(maxSaleAmountVal)) {
    readInDataWhenTargetHide(tiledFormDataRef.current, {
      label: `${maxSaleAmountVal}元（${saleMaxCom?.anotherName || saleMaxCom?.name}）`,
      value: saleMaxCom.identification,
      detailShowVal: `${maxSaleAmountVal}元`,
      countValue: maxSaleAmountVal, // 服务端用
    });
  }
};

/**
* @description 向销售额预测相关属性组件填入数值
* @param form
* @param targetCom 属性组件
* @param val 属性更新值，清空时为null
* @return
*/
const fillInEstimateComp = (form: any, targetCom: any, val: any) => {
  if (!form || !targetCom) return;

  setComValue(targetCom, val);
  // setFieldValue 不会触发onValuesChange
  form.setFieldValue(targetCom.identification, val); // 组件显示值
};

/**
 * @description 客群质量得分
 * @param addressInfo 地址信息
 * @return
 */
const updateCustomerQualityScore = async (
  tiledFormDataRef: any,
  form,
  addressInfo: any
) => {
  const targetCom = tiledFormDataRef.current.find((item: any) => item.identification === customerQualityScoreIdentification && item.controlType === ControlType.INPUT_NUMBER.value);
  if (!targetCom) return;

  // 详细地址信息
  const { lat, lng } = addressInfo;
  // 实时查询商圈信息
  const data = await chancepointFlowScore({ lat, lng });
  const val = (data && isDef(data.flowMatchIndex)) ? Math.round(data.flowMatchIndex) : null;
  setComValue(targetCom, val);
  // setFieldValue 不会触发onValuesChange
  form.setFieldValue(targetCom.identification, val); // 组件显示值
};

/**
 * 处理踩点组件的textValue
 */
export const handleFootprintTextValue = (
  tiledFormDataRef,
  targetIdentification,
  val
) => {
  const footprintIdentifications = ['dynamicComponent_footprint_isStart',
    'dynamicComponent_footprint_checkRule',
    'dynamicComponent_footprint_days',
    'dynamicComponent_footprint_checkPeriod'];
  if (!footprintIdentifications.includes(targetIdentification)) return;

  const targetCom = tiledFormDataRef.current.find((item: any) => item.identification === 'locationFootprint');
  let newVal = val;
  // 如果是踩点时间，对时间进行数据处理
  if (targetIdentification === 'dynamicComponent_footprint_checkPeriod') {
    newVal = [
      moment(val[0]).format('HH:mm'),
      moment(val[1]).format('HH:mm')
    ];
  }
  const oldValue = JSON.parse(targetCom?.textValue);
  const newValue = JSON.stringify({
    ...oldValue,
    [targetIdentification]: newVal
  });
  setComValue(targetCom, newValue);
  // updateRelateShow(targetIdentification);
};

/**
 * 详细地址组件的特殊逻辑
 * 通过高德的逆地址解析拿到高德的code，通过接口查找对应的省市区id，然后赋值到省市区组件上
 * 按照isInitChange，分为回显示地址处理，手动地址变动处理
 */
export const addressChangeCommon = async (
  params: any,
  tiledFormDataRef,
  form,
  busiPlanCallback: Function,
  busiInfoCallback: Function,
) => {
  const {
    isInitChange,
  } = params;

  if (isInitChange) { // 特殊情况，初始化时获取到地址，更新相关数据，
    addressInitChange(params, tiledFormDataRef, form, busiPlanCallback, busiInfoCallback);
  } else {
    addressManualChange(params, tiledFormDataRef, form, busiPlanCallback, busiInfoCallback);
  }
};

/**
 * 详细地址组件的特殊逻辑
 * 通过高德的逆地址解析拿到地址信息，然后通过城市名(getAMapCityName)查找对应的省市区id
 */
export const addressManualChange = async (
  params: any,
  tiledFormDataRef,
  form,
  busiPlanCallback: Function,
  busiInfoCallback: Function,
  /** 加盟商预填可捕获独立后台请求错误，普通手动地址链路保持既有行为。 */
  onBackgroundError?: (error: any) => void,
) => {
  const trackBackground = (task: Promise<any>) => {
    if (onBackgroundError) task.catch(onBackgroundError);
  };
  const {
    lng,
    lat,
    // cityName,
    name,
    poiId,
    identification, // 详细地址组件识别符
    isCluster,
  } = params;
  const addressInfo: any = await getLngLatAddress([lng, lat], '', false).catch((err) => console.log(`查询具体地址信息：${err}`));
  if (!addressInfo) return;
  const { addressComponent, formattedAddress } = addressInfo;
  const { province: pName, city: cName, district, adcode } = addressComponent || {};
  const shortAddress = removePcdFromAddress(formattedAddress, [pName, cName, district]);

  const cityName = getAMapCityName(addressComponent);
  const textVal = {
    // 这里之所以用name，是因为用formattedAddress数据那边大多数时候查不到
    address: name || formattedAddress,
    longitude: lng,
    latitude: lat,
    poiId,
    poiName: name,
    shortAddress,
    isCluster: !!isCluster,
  };
  const addressCom = tiledFormDataRef.current.find((item: any) => item.identification === identification);
  setComValue(addressCom, textVal); // 存储地址的的数据

  const pcdInfo: any = {};
  const cityResult: any = await areaList({ type: 1 });
  const cityForAMap = getCityForAMapData(cityResult);
  const cityInfo = cityForAMap?.find(item => item.name.indexOf(cityName) > -1);
  const { district: districtList, id: cityId, provinceId, provinceName } = cityInfo || {};
  pcdInfo.provinceId = provinceId;
  pcdInfo.provinceName = provinceName;
  pcdInfo.cityId = cityId;
  pcdInfo.cityName = cityName;
  // 20260210最初按name匹配的，现在看起来code更合适
  const replaceDistrcitName = findReplaceDistrcitName(district);
  let targetDistrict;
  if (replaceDistrcitName) {
    // 优先方案，为了让“两江新区”匹配到“渝北区”而不是“江北区”
    targetDistrict = districtList?.find((districtItem: any) => districtItem?.name?.includes(replaceDistrcitName));
  }
  if (!targetDistrict) {
    // 普通方案，按name，或者code匹配
    targetDistrict = districtList?.find((districtItem: any) => districtItem?.name?.includes(district) || adcode === districtItem.code);
  }
  if (targetDistrict) {
    const { id: districtId, name: districtName } = targetDistrict;
    pcdInfo.districtId = districtId;
    pcdInfo.districtName = districtName;
  }

  // 通过code查找对应的省市区id，然后赋值到省市区组件上
  // const { adcode } = addressComponent || {};
  // if (!adcode) return;
  // const pcdInfo = await codeToPCD({
  //   districtCode: adcode,
  //   cityName
  // });
  // 省市区组件
  const pcdCom = tiledFormDataRef.current.find((item: any) => item.identification === pcdIdentification);
  // const { provinceId, cityId, districtId } = pcdInfo;
  setComValue(pcdCom, pcdInfo); // 存储省市区的数据
  // setFieldValue 不会触发onValuesChange
  // 选择商圈后，将商圈的省市区赋值到省市区组件上 ControlType.AREA
  // 且这里指定了标识为 area
  form.setFieldValue(pcdIdentification, [provinceId, cityId, pcdInfo.districtId]); // 组件显示值

  // 周边查询组件
  const surroundAddress = {
    // 和丹哥确认过，这里的adcode似乎没有用到，先保留
    adcode,
    lat,
    lng,
    cityId,
    cityName,
    address: name || formattedAddress
  };
  trackBackground(updateAddressSurround(tiledFormDataRef, form, surroundAddress));

  const latLng = {
    lat,
    lng,
  };
  const cityLatLng = {
    lat,
    lng,
    cityId,
  };
  // 商圈规划组件
  trackBackground(updateAddressBusiPlan(tiledFormDataRef, form, latLng, busiPlanCallback));

  // 商圈信息组件
  const businessInfoCom = tiledFormDataRef.current.find((item: any) => item.controlType === ControlType.MATCH_BUSINESS_CIRCLE.value);
  if (businessInfoCom) {
    // 有匹配商圈组件，在获取到信息后再请求销售额预估 busiInfoCallback
    trackBackground(updateAddressBusiInfo(tiledFormDataRef, form, latLng, (busiInfo: any) => {
      busiInfoCallback(busiInfo);

      // 获取预估销售额、预估销售额上限及外卖销售额
      trackBackground(updateEstimatedSales(tiledFormDataRef, form, cityLatLng, busiInfo));
    }, false));
  } else {
    // 获取预估销售额、预估销售额上限及外卖销售额
    trackBackground(updateEstimatedSales(tiledFormDataRef, form, cityLatLng));
  }

  // “客群质量评分”
  trackBackground(updateCustomerQualityScore(tiledFormDataRef, form, cityLatLng));
};

/**
 * 详细地址组件的特殊逻辑
 *  与 addressManualChange 区别，编辑表单回显时获取到地址后，执行 addressInitChange ；之后变更地址执行 addressManualChange
 *
 *  20240311添加本事件的背景：
 *  创建机会点时，网规组件选择的商圈内没有集客点选项，提交机会点。
 *  之后新增了集客点，但是因为没有重新选择详细地址，没有重新触发重新获取网规商圈信息，
 *  导致网规组件的【所属集客点】字段一直显示【暂无集客点】字段。
 *
 */
export const addressInitChange = async (
  params: any,
  tiledFormDataRef,
  form,
  busiPlanCallback: Function,
  busiInfoCallback: Function,
) => {
  const {
    lng,
    lat,
  } = params;

  const latLng = {
    lat,
    lng,
  };
  // 商圈规划组件
  updateAddressBusiPlan(tiledFormDataRef, form, latLng, busiPlanCallback);

  // 商圈信息组件
  updateAddressBusiInfo(tiledFormDataRef, form, latLng, busiInfoCallback, true);
};
